import { motion } from 'framer-motion';
import { RefObject, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import debounce from '../../../common/utils/debounce';

interface Props {
  refs: { [name: string]: RefObject<any> };
  activeTabName: string;
  isAnimating: boolean;
  finishAnimating: () => void;
}

export default function TabUnderline({ refs, activeTabName, finishAnimating, isAnimating }: Props) {
  const [{ x, width }, setAttributes] = useState({ width: 0, x: 0 });

  const updateAttributes = useCallback(() => {
    if (!refs?.[activeTabName]?.current) return;
    setAttributes({
      x: refs[activeTabName].current.offsetLeft,
      width: refs[activeTabName].current.getBoundingClientRect().width,
    });
  }, [activeTabName, refs]);

  useEffect(() => {
    updateAttributes();
  }, [activeTabName, refs, updateAttributes]);

  useEffect(() => {
    const recalculateAttrs = debounce(() => {
      updateAttributes();
    }, 500);

    window.addEventListener('resize', recalculateAttrs);
    return () => {
      window.removeEventListener('resize', recalculateAttrs);
    };
  });

  return <UnderlineDiv animate={{ x, width }} $isAnimating={isAnimating} onAnimationComplete={finishAnimating} />;
}

const UnderlineDiv = styled(motion.div)<{ $isAnimating: boolean }>`
  position: absolute;
  left: 0;
  width: 0;
  height: 3px;
  background: #000000;
  opacity: ${({ $isAnimating }) => ($isAnimating ? 1 : 0)};
  bottom: -20px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    bottom: -16px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    bottom: -13px;
  }
`;
